import { useEffect, useState } from 'react'
import { registry, type LogEntry } from '../lib/registry'

/**
 * The ledger: what was actually done, and how to check it.
 *
 * Takeaways are the lessons; this is the provenance. A takeaway says "never
 * deploy without --env". An entry here says "on 31 August the username oracle
 * was closed on four production apps, here are the four commits, here is how it
 * was verified". One is advice, the other is what happened.
 *
 * Every entry carries EVIDENCE — a commit, a URL, a count — because an entry
 * nobody can check a year from now is a memory, and memories are the thing this
 * exists to stop relying on.
 */
const KINDS = ['ship', 'fix', 'security', 'audit', 'decision', 'finding', 'infra']

export default function Log() {
  const [items, setItems] = useState<LogEntry[]>([])
  const [open, setOpen] = useState(0)
  const [kind, setKind] = useState<string | null>(null)
  const [openOnly, setOpenOnly] = useState(false)

  useEffect(() => {
    void registry.log({ kind: kind ?? undefined, state: openOnly ? 'open' : undefined })
      .then((d) => { setItems(d.log); if (!kind && !openOnly) setOpen(d.open) })
  }, [kind, openOnly])

  // Grouped by day, because that is how the work actually happened.
  const days = items.reduce<Record<string, LogEntry[]>>((acc, e) => {
    ;(acc[e.happened_on] ||= []).push(e)
    return acc
  }, {})

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>Log</h1>
          <p className="muted">
            {items.length} entries{open > 0 && <>, <strong>{open} still open</strong></>}.
            What was done, with the commit or the URL that proves it.
          </p>
        </div>
      </header>

      <nav className="chips">
        <button className={!kind && !openOnly ? 'on' : ''} onClick={() => { setKind(null); setOpenOnly(false) }}>everything</button>
        {/* Open findings are the ones worth surfacing first. */}
        <button className={openOnly ? 'on' : ''} onClick={() => { setKind(null); setOpenOnly(true) }}>still open</button>
        {KINDS.map((k) => (
          <button key={k} className={kind === k ? 'on' : ''} onClick={() => { setKind(k); setOpenOnly(false) }}>{k}</button>
        ))}
      </nav>

      {Object.entries(days).map(([day, entries]) => (
        <div key={day} className="log-day">
          <h2 className="log-date">{day}</h2>
          <ul className="takeaways">
            {entries.map((e) => (
              <li key={e.id} className={e.state === 'open' ? 'scar' : ''}>
                <p className="rule">{e.summary}</p>
                {e.detail && <p className="why">{e.detail}</p>}
                <p className="meta">
                  <span className="chip">{e.kind}</span>
                  {e.state === 'open' && <span className="chip chip-scar">still open</span>}
                  {e.unit_name && <span className="muted small">{e.unit_name}</span>}
                  {e.component_name && <span className="muted small">· {e.component_name}</span>}
                  {e.evidence && <span className="muted small evidence">{e.evidence}</span>}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}
